import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Flame, Camera, Quote, Sparkles, Clock, MapPin, ExternalLink, MessageCircle, Wand2 } from "lucide-react";
import { TopNav } from "../components/TopNav";
import { Copilot } from "../components/Copilot";
import { TikTokInspiration } from "../components/TikTokInspiration";
import { ReelScriptGenerator } from "../components/ReelScriptGenerator";
import { ViralScore } from "../components/ViralScore";
import { fetchItinerary } from "../lib/api";
import { toast } from "sonner";

const mapUrl = (spot) => {
  if (spot.lat && spot.lng) return `https://www.google.com/maps/search/?api=1&query=${spot.lat},${spot.lng}`;
  const q = encodeURIComponent(`${spot.name} ${spot.city || ""} ${spot.country || ""}`);
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

export default function SpotPage() {
  const { itineraryId, spotId } = useParams();
  const navigate = useNavigate();
  const [itin, setItin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copilotSignal, setCopilotSignal] = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchItinerary(itineraryId)
      .then(setItin)
      .catch(() => toast.error("Could not load this trip"))
      .finally(() => setLoading(false));
  }, [itineraryId]);

  const spot = useMemo(() => (itin?.spots || []).find((s) => s.id === spotId), [itin, spotId]);
  const siblingSpots = useMemo(() => (itin?.spots || []).filter((s) => s.id !== spotId).slice(0, 4), [itin, spotId]);

  if (loading || !itin) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <TopNav />
        <div className="flex items-center gap-3 text-zinc-400">
          <Sparkles className="h-5 w-5 animate-pulse text-[#00F2FE]" /> Loading spot…
        </div>
      </div>
    );
  }

  if (!spot) {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <TopNav />
        <main className="pt-28 px-5 max-w-3xl mx-auto text-center">
          <h1 className="font-display font-black text-3xl">Spot not found</h1>
          <Link to={`/itinerary/${itineraryId}`} className="mt-4 inline-block text-[#00F2FE] underline">Back to itinerary</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <TopNav />

      {/* COVER */}
      <section className="relative w-full">
        <div className="relative h-[55svh] sm:h-[70svh] w-full overflow-hidden">
          <img src={spot.image} alt={spot.name} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 scrim-full" />
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10">
          <div className="mx-auto max-w-4xl px-5 sm:px-8 pb-8 sm:pb-12">
            <button
              onClick={() => navigate(`/itinerary/${itineraryId}`)}
              data-testid="spot-back"
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white mb-4"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {itin.title}
            </button>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <Flame className="h-3.5 w-3.5 text-[#FE0979]" />
                <span className="font-display font-bold text-xs">{spot.viral_score} / 100 viral</span>
              </span>
              <span className="glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-zinc-300" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">{spot.city}{spot.country ? `, ${spot.country}` : ""}</span>
              </span>
              {spot.time_of_day && (
                <span className="glass rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-[#00F2FE]" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em]">{spot.time_of_day}</span>
                </span>
              )}
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="font-display font-black tracking-tight text-4xl sm:text-6xl leading-[0.98] text-shadow-soft max-w-3xl"
            >
              {spot.name}
            </motion.h1>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {(spot.vibe_tags || []).map((t) => (
                <span key={t} className="text-[10px] font-bold uppercase tracking-[0.18em] bg-white/10 border border-white/15 rounded-full px-2.5 py-1">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DETAIL CARDS */}
      <section className="px-5 sm:px-8 py-12">
        <div className="mx-auto max-w-4xl">
          <div className="glass rounded-3xl p-5 sm:p-6 mb-5">
            <ViralScore
              breakdown={spot.viral_breakdown}
              fallbackScore={spot.viral_score}
              size="lg"
              showBreakdown
              testId="spot-viral-score"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailCard icon={<Clock className="h-4 w-4 text-[#00F2FE]" />} label="Best time to shoot" value={spot.best_time} />
            <DetailCard icon={<Camera className="h-4 w-4 text-[#00F2FE]" />} label="Best photo angle" value={spot.photo_angle} />
            <DetailCard icon={<Quote className="h-4 w-4 text-[#FE0979]" />} label="Suggested caption" value={`"${spot.reel_caption}"`} />
            <DetailCard icon={<Sparkles className="h-4 w-4 text-[#FE0979]" />} label="Best reel idea" value={spot.content_hook} />
          </div>
        </div>

        <div className="mx-auto max-w-4xl mt-6 space-y-3">
          <a
            href={mapUrl(spot)}
            target="_blank"
            rel="noreferrer"
            data-testid="spot-page-map-link"
            className="w-full inline-flex items-center justify-center gap-2 gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.2em] text-xs rounded-full py-3.5 hover:opacity-95 active:scale-[0.99] transition"
          >
            Open in Google Maps <ExternalLink className="h-4 w-4" />
          </a>
          
          <ReelScriptGenerator spot={spot} itineraryId={itineraryId} />
        </div>
      </section>

      {/* NEARBY */}
      {(() => {
        const nearbyList = Array.isArray(spot.nearby) ? spot.nearby : spot.nearby ? [spot.nearby] : [];
        return nearbyList.length > 0 ? (
          <section className="px-5 sm:px-8 pb-8">
            <div className="mx-auto max-w-4xl">
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-3">Nearby trending</div>
              <div className="flex flex-wrap gap-2">
                {nearbyList.map((n) => (
                  <span key={n} className="glass rounded-full px-3 py-1.5 text-xs font-medium">{n}</span>
                ))}
              </div>
            </div>
          </section>
        ) : null;
      })()}

      {/* TIKTOK INSPIRATION */}
      <TikTokInspiration spot={spot} prompt={itin.hero_prompt || itin.prompt || ""} />

      {/* PERSONALIZE WITH AI */}
      <section className="px-5 sm:px-8 pb-12">
        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden glass-strong rounded-3xl p-6 sm:p-8 ring-neon">
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-[#FE0979]/20 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-[#00F2FE]/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#FFB45C] mb-2 inline-flex items-center gap-2">
                <Wand2 className="h-3.5 w-3.5" /> Personalize this spot
              </div>
              <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight max-w-xl">
                Need this <span className="text-gradient">tailored</span> to your style?
              </h3>
              <p className="text-zinc-400 mt-2 text-sm sm:text-base max-w-xl">
                Ask the AI Copilot for caption variations, photo angles, what to wear, best timing — all
                grounded in <strong className="text-white">{spot.name}</strong>.
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  "Best photo angles here?",
                  "Write me 3 caption options",
                  "What time of day looks best?",
                  "Reel hook idea for this spot",
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => setCopilotSignal((n) => n + 1)}
                    data-testid={`spot-personalize-chip-${q.slice(0, 14).toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[11px] sm:text-xs font-medium text-zinc-200 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 transition"
                  >
                    {q}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCopilotSignal((n) => n + 1)}
                data-testid="open-copilot-spot-cta"
                className="mt-6 gradient-pink-cyan text-black font-display font-bold uppercase tracking-[0.18em] text-xs rounded-full px-5 py-3 inline-flex items-center gap-2 hover:opacity-95 active:scale-[0.98] transition"
              >
                <MessageCircle className="h-4 w-4" /> Ask AI about this spot
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SIBLING SPOTS */}
      {siblingSpots.length > 0 && (
        <section className="px-5 sm:px-8 pb-20">
          <div className="mx-auto max-w-5xl">
            <div className="flex items-end justify-between mb-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#00F2FE]">More from this trip</div>
              <Link to={`/itinerary/${itineraryId}`} className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 hover:text-white inline-flex items-center gap-2">
                See all <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {siblingSpots.map((s) => (
                <Link
                  key={s.id}
                  to={`/spot/${itineraryId}/${s.id}`}
                  data-testid={`sibling-spot-${s.id}`}
                  className="block relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 hover-lift group"
                >
                  <img src={s.image} alt={s.name} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 scrim-bottom" />
                  <div className="absolute top-2 right-2 glass rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                    <Flame className="h-3 w-3 text-[#FE0979]" />
                    <span className="font-display font-bold text-[10px]">{s.viral_score}</span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <div className="font-display font-bold text-sm leading-tight">{s.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Copilot
        itineraryId={itin.id}
        itineraryTitle={itin.title}
        spotName={spot.name}
        openSignal={copilotSignal}
        label="Ask AI about this spot"
      />
    </div>
  );
}

const DetailCard = ({ icon, label, value }) => (
  <div className="glass rounded-3xl p-5">
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400 mb-2 flex items-center gap-1.5">
      {icon} {label}
    </div>
    <p className="text-zinc-100 leading-relaxed text-sm font-body">{value}</p>
  </div>
);
